// components/keyboard/useKeyboardShortcuts.ts
import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { shortcutManager } from './shortcutManager';

export const useKeyboardShortcuts = () => {
  const location = useLocation();
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const lastKeyRef = useRef<string>('');

  const getMacNumberFromSpecialChar = (char: string, withShift: boolean = false): string | null => {
    const macSpecialCharMap: Record<string, string> = {
      // Your existing number mappings
      '¡': '1', '™': '2', '£': '3', '¢': '4', '§': '5',
      '†': '6', '¶': '7', '®': '8', 'º': '9', 'ª': '0',
      
      // Your existing letter mappings
      'þ': 't', 'Þ': 't', 'ß': 's', '©': 'g', '∫': 'b', '∂': 'd', 'ƒ': 'f',
      '˙': 'h', '¬': 'l', 'µ': 'm', 'ø': 'o', 'π': 'p', 'œ': 'q', '®': 'r',
      '√': 'v', '∑': 'w', '≈': 'x', '¥': 'y', 'Ω': 'z', 'æ': 'a', '´': 'e',
      '¨': 'u', 'ˆ': 'i', '˜': 'n', 'ç': 'c', '˚': 'k', '∆': 'j', '«': '\\', '»': '|',
      
      // 🎯 ADDED NEW MAPPINGS FOR NON-WORKING KEYS:
      'å': 'a', // Cmd+Option+A - Select All
      '‚': '2', 'ƒ': '3', '„': '4', '…': '5', '†': '6', '€': 'e', '‡': 'd',
      
      // 🆕 CRITICAL: Add mappings for Cmd+Option+Shift combinations
      'Å': 'a', // Cmd+Option+Shift+A
      'Î': 'i', // Cmd+Option+Shift+I  
      'Ç': 'c', // Cmd+Option+Shift+C
      'Ë': 'e', // Cmd+Option+Shift+E
      '‰': 'r', // Cmd+Option+Shift+R
      'Á': 'a', // Additional A mapping
      'È': 'e', // Additional E mapping
      'Í': 'i', // Additional I mapping
      'Â': 'a', // Additional A mapping
      'Ê': 'e', // Additional E mapping
      'Î': 'i', // Additional I mapping
      'Ô': 'o', // Additional O mapping
      'Û': 'u', // Additional U mapping
      
      // 🎯 EXTREMELY IMPORTANT: Add comprehensive E mappings for Cmd+Alt+Shift+E
      'É': 'e', // Cmd+Option+Shift+E (French E acute)
      'È': 'e', // Cmd+Option+Shift+E (French E grave) 
      'Ê': 'e', // Cmd+Option+Shift+E (French E circumflex)
      'Ë': 'e', // Cmd+Option+Shift+E (French E diaeresis)
      'Ē': 'e', // Cmd+Option+Shift+E (E macron)
      'Ė': 'e', // Cmd+Option+Shift+E (E dot above)
      'Ę': 'e', // Cmd+Option+Shift+E (E ogonek)
    };
    
    return macSpecialCharMap[char] || null;
  };

  const isInputElement = (element: EventTarget | null): boolean => {
    if (!element) return false;
    const tagName = (element as HTMLElement).tagName?.toLowerCase();
    const isContentEditable = (element as HTMLElement).isContentEditable;
    
    return (
      tagName === 'input' ||
      tagName === 'textarea' ||
      tagName === 'select' ||
      isContentEditable
    );
  };

  const isContextMatch = useCallback((shortcut: any) => {
    if (!shortcut.context || shortcut.global) {
      return true;
    }
    
    const currentPath = location.pathname.startsWith('/') 
      ? location.pathname.slice(1) 
      : location.pathname;
    
    return shortcut.context.some((contextPath: string) => {
      const normalizedContext = contextPath.startsWith('/') 
        ? contextPath.slice(1) 
        : contextPath;
      
      return currentPath === normalizedContext || 
             currentPath.startsWith(normalizedContext + '/');
    });
  }, [location.pathname]);

  const matchesShortcut = useCallback((
    event: KeyboardEvent,
    shortcutCombo: string,
    key: string
  ): boolean => {
    const parts = shortcutCombo.toLowerCase().split('+');
    
    // Special handling for "?" key
    if (shortcutCombo === '?') {
      return event.key === '?' || (event.shiftKey && event.key === '/');
    }

    // Build required state
    const required = {
      ctrl: parts.includes('ctrl'),
      cmd: parts.includes('cmd'),
      alt: parts.includes('alt') || parts.includes('option'),
      shift: parts.includes('shift'),
      key: parts.find(p => !['ctrl', 'cmd', 'alt', 'option', 'shift'].includes(p)) || '',
    };

    // Build actual state
    const actual = {
      ctrl: event.ctrlKey,
      cmd: event.metaKey,
      alt: event.altKey,
      shift: event.shiftKey,
      key: key,
    };

    // Match modifiers
    if (required.ctrl !== actual.ctrl) return false;
    if (required.cmd !== actual.cmd) return false;
    if (required.alt !== actual.alt) return false;
    if (required.shift !== actual.shift) return false;

    // Match key
    if (required.key) {
      // Special keys
      if (required.key === 'escape' && actual.key !== 'escape') return false;
      if (required.key === 'delete' && actual.key !== 'delete' && actual.key !== 'backspace') return false;
      if (required.key === '?' && actual.key !== '?') return false;
      
      // Numbers and letters
      if (required.key !== actual.key) return false;
    }

    return true;
  }, []);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (isInputElement(event.target)) {
      return;
    }

    // Store the code for dead key handling
    let key = event.key.toLowerCase();
    let actualKey = key;

    // Handle dead keys on Mac (Cmd+Alt+letter combinations)
    if (key === 'dead' || key === 'unidentified') {
      // Use event.code to get the physical key
      if (event.code.startsWith('Key')) {
        actualKey = event.code.replace('Key', '').toLowerCase();
       (`🔧 Dead key detected, using code: ${event.code} → ${actualKey}`);
      } else if (event.code.startsWith('Digit')) {
        actualKey = event.code.replace('Digit', '');
       (`🔧 Dead key detected, using code: ${event.code} → ${actualKey}`);
      } else {
       (`⚠️ Dead key with unknown code: ${event.code}`);
        return;
      }
    }

    // Enhanced macOS special character handling for BOTH numbers and letters
    if (isMac && event.metaKey && event.altKey) {
      const mappedKey = getMacNumberFromSpecialChar(event.key, event.shiftKey);
      if (mappedKey) {
       (`🎯 Fixed Mac dead key ${event.key} → ${mappedKey} (shift: ${event.shiftKey})`);
        actualKey = mappedKey;
      } else {
        // Debug: Log what character we're getting for shift combinations
        if (event.shiftKey) {
         (`🔍 Cmd+Alt+Shift+? produced unmapped character: "${event.key}" (charCode: ${event.key.charCodeAt(0)})`);
          
          // 🆕 FALLBACK: If no mapping found, try using event.code for physical key
          if (event.code.startsWith('Key')) {
            const physicalKey = event.code.replace('Key', '').toLowerCase();
           (`🔧 Using physical key from event.code: ${physicalKey}`);
            actualKey = physicalKey;
          }
        }
      }
    }

    const shortcuts = shortcutManager.getShortcuts();
    
    // SPECIFIC DEBUG FOR T AND S KEYS
    if ((actualKey === 't' || actualKey === 's' || actualKey === 'g') && (event.ctrlKey || event.metaKey) && event.altKey) {
     ('🔧 T/S/G KEY PROCESSING:', {
        originalEventKey: event.key,
        finalActualKey: actualKey,
        code: event.code,
        meta: event.metaKey,
        alt: event.altKey,
        path: location.pathname
      });
    }

   (`🎹 Key: ${actualKey} (original: ${key}), Ctrl: ${event.ctrlKey}, Cmd: ${event.metaKey}, Alt: ${event.altKey}, Shift: ${event.shiftKey}, Code: ${event.code}`);
   (`📍 Path: ${location.pathname}, Shortcuts: ${shortcuts.length}`);

    // ============================================================================
    // ENHANCED DEBUG FOR NON-WORKING SHORTCUTS - ADDED SECTION
    // ============================================================================

    // Debug for specific problematic shortcuts
    if ((event.ctrlKey || event.metaKey) && event.altKey) {
     ('🔍 NON-WORKING SHORTCUTS ANALYSIS:');
      
      // Check for specific non-working shortcuts
      const problemKeys = ['a', 'd', 'e', 'i', 'c', 'r'];
      if (problemKeys.includes(actualKey)) {
       (`   ⚠️ Problem key detected: ${actualKey}`);
        
        // Find all shortcuts that should match this key
        const potentialMatches = shortcuts.filter(shortcut => {
          const platformKeys = isMac ? shortcut.keys.macos : shortcut.keys.windows;
          return platformKeys.some(combo => combo.includes(actualKey));
        });
        
       (`   📋 Potential matches for ${actualKey}:`, potentialMatches.map(s => ({
          id: s.id,
          name: s.name,
          context: s.context,
          keys: isMac ? s.keys.macos : s.keys.windows
        })));
        
        // Check context matching for each potential match
        potentialMatches.forEach(shortcut => {
          const contextMatch = isContextMatch(shortcut);
         (`   🔍 ${shortcut.id}: contextMatch=${contextMatch}, path=${location.pathname}`);
        });
      }
    }

    // Enhanced macOS dead key detection for problematic keys
    if (isMac && event.metaKey && event.altKey && !event.shiftKey) {
      const problematicCharacters = ['å', '∂', '´', 'ˆ', 'ç', '®'];
      if (problematicCharacters.includes(event.key)) {
       (`🎯 PROBLEMATIC MAC CHARACTER: ${event.key}`);
        
        // Try to map additional macOS special characters
        const extendedMacMap: Record<string, string> = {
          'å': 'a', // Cmd+Option+A
          '∂': 'd', // Cmd+Option+D  
          '´': 'e', // Cmd+Option+E
          'ˆ': 'i', // Cmd+Option+I
          'ç': 'c', // Cmd+Option+C
          '®': 'r', // Cmd+Option+R
        };
        
        const mappedKey = extendedMacMap[event.key];
        if (mappedKey) {
         (`🔄 Fixed problematic char ${event.key} → ${mappedKey}`);
          actualKey = mappedKey;
        }
      }
    }

    // ============================================================================
    // ENHANCED SHIFT COMBINATION DEBUGGING
    // ============================================================================

    // Debug specifically for Cmd+Alt+Shift combinations
    if (isMac && event.metaKey && event.altKey && event.shiftKey) {
     ('🎯 CMD+ALT+SHIFT COMBINATION ANALYSIS:');
     ('   Event Details:', {
        key: event.key,
        code: event.code,
        charCode: event.key.charCodeAt(0),
        actualKey: actualKey,
        shiftKey: event.shiftKey
      });
      
      // Test specific problematic shortcuts
      const shiftProblemKeys = ['e', 'i', 'c', 's'];
      if (shiftProblemKeys.includes(actualKey)) {
       (`   🔍 Testing Cmd+Alt+Shift+${actualKey.toUpperCase()}:`);
        
        const potentialShiftMatches = shortcuts.filter(shortcut => {
          const platformKeys = isMac ? shortcut.keys.macos : shortcut.keys.windows;
          return platformKeys.some(combo => 
            combo.includes('shift') && combo.includes(actualKey)
          );
        });
        
       (`   📋 Found ${potentialShiftMatches.length} potential shift matches:`);
        potentialShiftMatches.forEach(shortcut => {
          const contextMatch = isContextMatch(shortcut);
         (`      - ${shortcut.id}: contextMatch=${contextMatch}, keys=${isMac ? shortcut.keys.macos : shortcut.keys.windows}`);
          
          // 🆕 EXTRA DEBUG: Check if the callback exists and is a function
          if (contextMatch) {
           (`      - Callback type: ${typeof shortcut.callback}`);
           (`      - Callback available: ${!!shortcut.callback}`);
          }
        });
        
        // 🆕 If no matches found, show ALL shortcuts with shift for debugging
        if (potentialShiftMatches.length === 0) {
         ('   🔍 No shift matches found. Showing ALL shift shortcuts:');
          const allShiftShortcuts = shortcuts.filter(shortcut => {
            const platformKeys = isMac ? shortcut.keys.macos : shortcut.keys.windows;
            return platformKeys.some(combo => combo.includes('shift'));
          });
          allShiftShortcuts.forEach(shortcut => {
           (`      - ${shortcut.id}: ${isMac ? shortcut.keys.macos : shortcut.keys.windows}`);
          });
        }
      }
    }

    // Special debug for Cmd+Alt+Shift+S (the working one)
    if (isMac && event.metaKey && event.altKey && event.shiftKey && actualKey === 's') {
     ('🔧 WHY Cmd+Alt+Shift+S WORKS:');
      const sShortcuts = shortcuts.filter(shortcut => 
        shortcut.keys.macos.some(combo => combo.includes('shift') && combo.includes('s'))
      );
      sShortcuts.forEach(shortcut => {
        const contextMatch = isContextMatch(shortcut);
       (`   ${shortcut.id}: contextMatch=${contextMatch}, callback=${typeof shortcut.callback}`);
      });
    }

    // Special debug for Cmd+Alt+Shift+E (the problematic one)
    if (isMac && event.metaKey && event.altKey && event.shiftKey && actualKey === 'e') {
     ('🔧 Cmd+Alt+Shift+E SPECIFIC DEBUG:');
      const eShortcuts = shortcuts.filter(shortcut => 
        shortcut.keys.macos.some(combo => combo.includes('shift') && combo.includes('e'))
      );
     (`   Found ${eShortcuts.length} E shift shortcuts:`);
      eShortcuts.forEach(shortcut => {
        const contextMatch = isContextMatch(shortcut);
       (`   - ${shortcut.id}: contextMatch=${contextMatch}, callback=${typeof shortcut.callback}, keys=${shortcut.keys.macos}`);
      });
    }

    // Debug for Shift key combinations
    if ((event.ctrlKey || event.metaKey) && event.altKey && event.shiftKey) {
     ('🔧 SHIFT COMBINATION DEBUG:', {
        key: actualKey,
        shift: event.shiftKey,
        path: location.pathname
      });
      
      // Check if shift combinations are being detected
      const shiftShortcuts = shortcuts.filter(shortcut => {
        const platformKeys = isMac ? shortcut.keys.macos : shortcut.keys.windows;
        return platformKeys.some(combo => combo.includes('shift') && combo.includes(actualKey));
      });
      
     (`   Shift shortcuts for ${actualKey}:`, shiftShortcuts.length);
    }

    // ============================================================================
    // END OF ADDED CODE - YOUR EXISTING LOGIC CONTINUES BELOW
    // ============================================================================

    let foundMatch = false;
    let contextMismatch = false;

    for (const shortcut of shortcuts) {
      if (shortcut.enabled === false) continue;

      const platformKeys = isMac ? shortcut.keys.macos : shortcut.keys.windows;
      
      for (const combo of platformKeys) {
        if (matchesShortcut(event, combo, actualKey)) {
          const contextMatch = isContextMatch(shortcut);
          
          if (contextMatch) {
           (`✅ MATCHED: ${shortcut.name} (${combo}) on context: ${shortcut.context}`);
            event.preventDefault();
            event.stopPropagation();
            
            // 🆕 EXTRA SAFETY: Check if callback exists before calling
            if (typeof shortcut.callback === 'function') {
              shortcut.callback();
              foundMatch = true;
              return;
            } else {
             (`❌ CALLBACK ERROR: ${shortcut.name} has no valid callback function`);
            }
          } else {
           (`🔍 CONTEXT MISMATCH: ${shortcut.name} - expected ${shortcut.context}, current path: ${location.pathname}`);
            contextMismatch = true;
          }
        }
      }
    }

    if (!foundMatch && (actualKey === 't' || actualKey === 's') && (event.ctrlKey || event.metaKey) && event.altKey) {
      if (contextMismatch) {
       (`❌ T/S KEY: Context mismatch - Make sure you're on the correct page`);
      } else {
       (`❌ T/S KEY: No shortcut found for this key combination`);
      }
    }

    if (!foundMatch) {
     (`❌ No shortcut matched`);
    }

    if (location.pathname.includes('tasks')) {
     ('🔍 TASKS PAGE CONTEXT DEBUG:');
     ('   - Current path:', location.pathname);
     ('   - Expected contexts: tasks, team-tasks, all-tasks');
      
      const taskShortcuts = shortcuts.filter(s => 
        s.context?.some(ctx => ctx === 'tasks' || ctx === 'team-tasks' || ctx === 'all-tasks')
      );
     ('   - Task shortcuts found:', taskShortcuts.length);
      
      taskShortcuts.forEach(shortcut => {
        const contextMatch = isContextMatch(shortcut);
       (`   - ${shortcut.id}: contextMatch=${contextMatch}`);
      });
    }

  }, [isMac, isContextMatch, matchesShortcut, location.pathname]);

  useEffect(() => {
   ('⌨️ Keyboard shortcuts initialized');
   (`📍 Current path: ${location.pathname}`);
   (`💻 Platform: ${isMac ? 'macOS' : 'Windows/Linux'}`);
    
    const shortcuts = shortcutManager.getShortcuts();
   (`📊 Total shortcuts: ${shortcuts.length}`);
    
    // Log specific T and S shortcuts for debugging
    const tShortcuts = shortcuts.filter(s => 
      s.keys.macos.some(k => k.includes('t')) || s.keys.windows.some(k => k.includes('t'))
    );
    const sShortcuts = shortcuts.filter(s => 
      s.keys.macos.some(k => k.includes('s')) || s.keys.windows.some(k => k.includes('s'))
    );
    
   (`🔧 T shortcuts:`, tShortcuts.map(s => ({ id: s.id, context: s.context, keys: s.keys })));
   (`🔧 S shortcuts:`, sShortcuts.map(s => ({ id: s.id, context: s.context, keys: s.keys })));

    // Log shift shortcuts specifically
    const shiftShortcuts = shortcuts.filter(s => 
      s.keys.macos.some(k => k.includes('shift')) || s.keys.windows.some(k => k.includes('shift'))
    );
   (`🔧 Shift shortcuts:`, shiftShortcuts.map(s => ({ 
      id: s.id, 
      name: s.name,
      context: s.context, 
      keys: isMac ? s.keys.macos : s.keys.windows 
    })));

    // 🆕 SPECIFIC DEBUG: Show export-tasks shortcut details
    const exportShortcut = shortcuts.find(s => s.id === 'export-tasks');
    if (exportShortcut) {
     ('🔧 EXPORT-TASKS SHORTCUT DETAILS:', {
        id: exportShortcut.id,
        name: exportShortcut.name,
        context: exportShortcut.context,
        keys: exportShortcut.keys,
        callback: typeof exportShortcut.callback
      });
    }

    document.addEventListener('keydown', handleKeyDown, { capture: true });
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [handleKeyDown, location.pathname, isMac]);
};